import React, { useMemo, useState } from 'react';
import {
  Add,
  Assignment,
  CalendarToday,
  CheckCircle,
  Delete,
  Edit,
  Schedule,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  ContractTask,
  useCreateContractTaskMutation,
  useGetContractTasksQuery,
  useUpdateContractTaskMutation,
} from '@/apis/extended/tasksApi';
import GlassButton from '@/components/ui/GlassButton';
import GlassCard from '@/components/ui/GlassCard';

interface TaskManagementProps {
  userRole: 'buyer' | 'seller' | 'agent';
  contractId: string;
}

const TaskManagement: React.FC<TaskManagementProps> = ({
  userRole,
  contractId,
}) => {
  const { data, isLoading } = useGetContractTasksQuery(contractId, {
    skip: !contractId,
  });
  const [createTask, { isLoading: isCreating }] =
    useCreateContractTaskMutation();
  const [updateTask, { isLoading: isUpdating }] =
    useUpdateContractTaskMutation();

  const tasks = (data?.data || []) as ContractTask[];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ContractTask | null>(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    dueDate: '',
    category: '',
    priority: 'medium' as 'high' | 'medium' | 'low',
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'default';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle />;
      case 'in_progress':
        return <Schedule />;
      case 'pending':
        return <Assignment />;
      case 'overdue':
        return <Warning />;
      default:
        return <Assignment />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleTaskStatusChange = (
    taskId: string,
    status: 'pending' | 'in_progress' | 'completed'
  ) => {
    updateTask({ taskId, updateData: { status } })
      .unwrap()
      .catch((e) => {
        console.error(e);
        alert('Failed to update task.');
      });
  };

  const handleCreateTask = () => {
    createTask({
      contractId,
      title: newTask.title,
      description: newTask.description,
      category: newTask.category || 'other',
      priority: newTask.priority,
      dueDate: newTask.dueDate
        ? new Date(newTask.dueDate).toISOString()
        : undefined,
      assignedToRole: userRole,
    })
      .unwrap()
      .then(() => {
        setDialogOpen(false);
        setNewTask({
          title: '',
          description: '',
          dueDate: '',
          category: '',
          priority: 'medium',
        });
      })
      .catch((e) => {
        console.error(e);
        alert('Failed to create task.');
      });
  };

  const handleEditTask = (task: ContractTask) => {
    setEditingTask(task);
    setNewTask({
      title: task.title,
      description: task.description || '',
      dueDate: task.dueDate
        ? new Date(task.dueDate).toISOString().split('T')[0]
        : '',
      category: task.category || '',
      priority: task.priority,
    });
    setDialogOpen(true);
  };

  const handleUpdateTask = () => {
    if (editingTask) {
      updateTask({
        taskId: editingTask._id,
        updateData: {
          title: newTask.title,
          description: newTask.description,
          dueDate: newTask.dueDate
            ? new Date(newTask.dueDate).toISOString()
            : undefined,
          category: newTask.category || 'other',
          priority: newTask.priority,
        },
      })
        .unwrap()
        .then(() => {
          setEditingTask(null);
          setDialogOpen(false);
          setNewTask({
            title: '',
            description: '',
            dueDate: '',
            category: '',
            priority: 'medium',
          });
        })
        .catch((e) => {
          console.error(e);
          alert('Failed to update task.');
        });
    }
  };

  const handleDeleteTask = (taskId: string) => {
    // Backend delete endpoint not implemented yet; keep UI intact but disable action.
    console.warn('Delete task requested (not implemented):', taskId);
  };

  const openCreateDialog = () => {
    setEditingTask(null);
    setNewTask({
      title: '',
      description: '',
      dueDate: '',
      category: '',
      priority: 'medium',
    });
    setDialogOpen(true);
  };

  // Filter tasks by user role
  const userTasks = useMemo(() => {
    return tasks.filter(
      (task) => task.assignedToRole === userRole || userRole === 'agent'
    );
  }, [tasks, userRole]);

  // Calculate statistics
  const completedTasks = userTasks.filter(
    (task) => task.status === 'completed'
  ).length;
  const pendingTasks = userTasks.filter(
    (task) => task.status === 'pending'
  ).length;
  const overdueTasks = userTasks.filter((task) => {
    if (!task.dueDate) return false;
    const dueDate = new Date(task.dueDate);
    const now = new Date();
    return dueDate < now && task.status !== 'completed';
  }).length;

  return (
    <Box>
      {/* Task Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.primary">
                {completedTasks}
              </Typography>
              <Typography variant="body2" color="text.primary">
                Completed Tasks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.primary">
                {pendingTasks}
              </Typography>
              <Typography variant="body2" color="text.primary">
                Pending Tasks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.primary">
                {overdueTasks}
              </Typography>
              <Typography variant="body2" color="text.primary">
                Overdue Tasks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Overdue Tasks Alert */}
      {overdueTasks > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.primary">
            {overdueTasks} Overdue Task{overdueTasks > 1 ? 's' : ''}
          </Typography>
          <Typography variant="body2" color="text.primary">
            Please complete overdue tasks immediately to avoid delays in your
            transaction.
          </Typography>
        </Alert>
      )}

      {/* Task List */}
      <GlassCard variant="medium">
        <CardHeader
          title="Tasks"
          action={
            <GlassButton
              glassVariant="bold"
              startIcon={<Add />}
              onClick={openCreateDialog}
            >
              Add Task
            </GlassButton>
          }
        />
        <CardContent>
          <List>
            {userTasks.map((task) => {
              const isOverdue =
                (task.dueDate ? new Date(task.dueDate) < new Date() : false) &&
                task.status !== 'completed';
              const finalStatus = isOverdue ? 'overdue' : task.status;

              return (
                <ListItem key={task._id} divider>
                  <ListItemIcon>
                    <Checkbox
                      checked={finalStatus === 'completed'}
                      onChange={(e) =>
                        handleTaskStatusChange(
                          task._id,
                          e.target.checked ? 'completed' : 'pending'
                        )
                      }
                    />
                  </ListItemIcon>
                  <ListItemIcon>{getStatusIcon(finalStatus)}</ListItemIcon>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        flexWrap="wrap"
                      >
                        <span
                          style={{
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            textDecoration:
                              finalStatus === 'completed'
                                ? 'line-through'
                                : 'none',
                            opacity: finalStatus === 'completed' ? 0.6 : 1,
                          }}
                        >
                          {task.title}
                        </span>
                        <Chip
                          label={finalStatus.replace('_', ' ')}
                          color={getStatusColor(finalStatus) as any}
                          size="small"
                        />
                        <Chip
                          label={task.category}
                          color="primary"
                          size="small"
                        />
                        <Chip
                          label={task.priority}
                          color={getPriorityColor(task.priority) as any}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <span
                          style={{
                            fontSize: '0.875rem',
                            color: 'rgba(255, 255, 255, 0.7)',
                          }}
                        >
                          {task.description}
                        </span>
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                          <CalendarToday fontSize="small" color="action" />
                          <span
                            style={{
                              fontSize: '0.75rem',
                              color: 'rgba(255, 255, 255, 0.7)',
                            }}
                          >
                            Due:{' '}
                            {task.dueDate
                              ? new Date(task.dueDate).toLocaleDateString()
                              : 'N/A'}
                          </span>
                          {task.completedAt && (
                            <>
                              <CheckCircle fontSize="small" color="success" />
                              <span
                                style={{
                                  fontSize: '0.75rem',
                                  color: 'rgba(255, 255, 255, 0.7)',
                                }}
                              >
                                Completed:{' '}
                                {new Date(
                                  task.completedAt
                                ).toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                  <Box>
                    <Tooltip title="Edit Task">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTask(task)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Task">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTask(task._id)}
                          disabled
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </CardContent>
      </GlassCard>

      {/* Create/Edit Task Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Create New Task'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Task Title"
            fullWidth
            value={newTask.title}
            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newTask.description}
            onChange={(e) =>
              setNewTask({ ...newTask, description: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                margin="dense"
                label="Due Date"
                type="date"
                fullWidth
                value={newTask.dueDate}
                onChange={(e) =>
                  setNewTask({ ...newTask, dueDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newTask.priority}
                  onChange={(e) =>
                    setNewTask({
                      ...newTask,
                      priority: e.target.value as 'high' | 'medium' | 'low',
                    })
                  }
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={newTask.category}
              label="Category"
              onChange={(e) =>
                setNewTask({ ...newTask, category: e.target.value as any })
              }
            >
              <MenuItem value="disclosures">Disclosures</MenuItem>
              <MenuItem value="contract">Contract</MenuItem>
              <MenuItem value="title">Title</MenuItem>
              <MenuItem value="insurance">Insurance</MenuItem>
              <MenuItem value="mortgage">Mortgage</MenuItem>
              <MenuItem value="inspection">Inspection</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setDialogOpen(false)}>Cancel</GlassButton>
          <GlassButton
            onClick={editingTask ? handleUpdateTask : handleCreateTask}
            glassVariant="bold"
            disabled={!newTask.title || !newTask.description}
          >
            {editingTask ? 'Update' : 'Create'}
          </GlassButton>
        </DialogActions>
      </Dialog>

      {isLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Loading tasks…
        </Alert>
      )}
    </Box>
  );
};

export default TaskManagement;
